import { Flex, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import Sparkline from '@/components/chart/Sparkline'
import CoinIcon from '@/components/CoinIcon'
import { useI18nRouter } from '@/i18n/navigation'
import { coinPricesByIdQueryOptions } from '@/services/coin/prices'
import { CoinId } from '@/types/coin'
import { currencyFormatter, toFixed } from '@/utils/number'

import type { CoinItem } from './hooks'
import styles from './index.module.css'

interface CoinRowProps {
  coin: CoinItem
}

function usePercentChangeInfo(percentChange: null | number) {
  const displayChangeValue = Number(toFixed((percentChange ?? 0) * 100))
  const percentChangeColor = (() => {
    if (displayChangeValue > 0) {
      return 'var(--common-success)'
    }
    if (displayChangeValue < 0) {
      return 'var(--common-danger)'
    }
    return 'var(--content-secondary)'
  })()
  const percentChangeText = (() => {
    if (percentChange === null) {
      return ''
    }
    let prefix = ''
    if (displayChangeValue > 0) {
      prefix = '+'
    }
    return `${prefix}${displayChangeValue}%`
  })()
  return {
    percentChangeColor,
    percentChangeText,
  }
}

function PriceChart({
  coinId,
  percentChangeColor,
}: {
  coinId: CoinId
  percentChangeColor: string
}) {
  const [inViewport, setInViewport] = useState(false)
  const { data } = useQuery({
    ...coinPricesByIdQueryOptions({
      interval: 'd',
      coinId,
    }),
    enabled: inViewport,
  })
  const prices = data?.prices?.map(price => price[1]) || []
  return (
    <Sparkline
      w={66}
      h={32}
      data={prices}
      inViewOptions={{ onInView: () => !inViewport && setInViewport(true) }}
      style={{
        pointerEvents: 'none',
      }}
      c={percentChangeColor}
    />
  )
}

export default function CoinRow({ coin }: CoinRowProps) {
  const router = useI18nRouter()
  const { percentChangeColor, percentChangeText } = usePercentChangeInfo(coin.percentChange)
  const { symbol, value } = currencyFormatter('', coin.usdPrice)
  const onClickRow = () => {
    router.push(`/${coin.slug}`)
  }

  return (
    <Flex className={styles.coinRow} onClick={onClickRow}>
      <CoinIcon coin={coin} />
      <Stack gap={2} ml={8} flex={3}>
        <Text variant={'body'}>{coin.name}</Text>
        <Text variant={'subtitle'} c={'var(--content-secondary)'}>
          {coin.symbol}
        </Text>
      </Stack>
      <PriceChart coinId={coin.id} percentChangeColor={percentChangeColor} />
      <Stack gap={2} flex={2} align={'flex-end'}>
        <Text variant={'body'}>{`${symbol}${value}`}</Text>
        <Text variant={'subtitle'} c={percentChangeColor}>
          {percentChangeText}
        </Text>
      </Stack>
    </Flex>
  )
}
